import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { captureReferralCode, clearReferralCode } from "./lib/referral";
import { BottomNav } from "./components/bottom-nav";
import { VenueHeader } from "./components/venue-header";
import { VenueAppShell } from "./components/venue-app-shell";
import { VenueWebLayout } from "./components/venue-web-layout";
import { ArtistSite, ArtistGalleryPage, ArtistNewsPage } from "./components/artist-site";
import { PromoterSite } from "./components/promoter-site";
import { SitePagesSite } from "./components/site-pages";
import { pageForPath } from "./lib/site-pages";
import { PullToRefresh } from "./components/pull-to-refresh";
import { OfflineBanner } from "./components/offline-banner";
import { VenueShell } from "./components/venue-shell";
import { DraftGate, DraftBanner } from "./components/draft-gate";
import { RecordWebLayout } from "./components/record-web-layout";
import { FestivalWebLayout } from "./components/festival-web-layout";
import { useVenue } from "./lib/venue";
import { isNativeApp } from "./lib/platform";
// Eager (in the entry chunk): the core landing path. These render as JSX inside
// the venue-home / festival / tickets orchestrators below, so they can't suspend
// mid-render, and they're the pages a first visitor lands on, keep them fast.
import { VenuePage } from "./pages/venue";
import { TicketsPage } from "./pages/tickets";
import { FestivalPage, type FestivalVariant } from "./pages/festival";
import { Navigate } from "@tanstack/react-router";
import { RadioShell } from "./components/radio-shell";
import {
  RadioHomePage,
  RadioSchedulePage,
  RadioShowsPage,
  RadioShopPage,
  RadioSupportPage,
} from "./pages/radio-home";
import { ComingSoonSplash } from "./pages/coming-soon-splash";

// Lazy routes: each page is its own chunk, fetched on navigation and prefetched
// on hover/touch (defaultPreload:"intent" below). This keeps the initial load,
// especially the public marketing/venue/festival landing, from parsing the
// whole app (the ~20-page admin cluster, commerce, wallet, QR scanning) up front.
const AllTiersPage = lazyRouteComponent(() => import("./pages/tiers"), "AllTiersPage");
const BenefitsPage = lazyRouteComponent(() => import("./pages/benefits"), "BenefitsPage");
const NoticeboardPage = lazyRouteComponent(() => import("./pages/noticeboard"), "NoticeboardPage");
const SignInPage = lazyRouteComponent(() => import("./pages/sign-in"), "SignInPage");
const SignUpPage = lazyRouteComponent(() => import("./pages/sign-up"), "SignUpPage");
const AccountPage = lazyRouteComponent(() => import("./pages/account"), "AccountPage");
const BookingsPage = lazyRouteComponent(() => import("./pages/bookings"), "BookingsPage");
const TrialPage = lazyRouteComponent(() => import("./pages/trial"), "TrialPage");
const ProfilePage = lazyRouteComponent(() => import("./pages/profile"), "ProfilePage");
const AdminPage = lazyRouteComponent(() => import("./pages/admin"), "AdminPage");
const AdminBrandingPage = lazyRouteComponent(() => import("./pages/admin-branding"), "AdminBrandingPage");
const AdminNoticeboardPage = lazyRouteComponent(() => import("./pages/admin-noticeboard"), "AdminNoticeboardPage");
const TicketDetailPage = lazyRouteComponent(() => import("./pages/ticket-detail"), "TicketDetailPage");
const BookEntry = lazyRouteComponent(() => import("./pages/book-entry"), "BookEntry");
const EventsPage = lazyRouteComponent(() => import("./pages/events"), "EventsPage");
const EventDetailPage = lazyRouteComponent(() => import("./pages/event-detail"), "EventDetailPage");
const MenuPage = lazyRouteComponent(() => import("./pages/menu"), "MenuPage");
const RecordDetailPage = lazyRouteComponent(() => import("./pages/record-detail"), "RecordDetailPage");
const CartPage = lazyRouteComponent(() => import("./pages/cart"), "CartPage");
const WishlistPage = lazyRouteComponent(() => import("./pages/wishlist"), "WishlistPage");
const AdminInventoryPage = lazyRouteComponent(() => import("./pages/admin-inventory"), "AdminInventoryPage");
const AdminRadioPage = lazyRouteComponent(() => import("./pages/admin-radio"), "AdminRadioPage");
const AdminStorefrontPage = lazyRouteComponent(() => import("./pages/admin-storefront"), "AdminStorefrontPage");
const AdminOffersPage = lazyRouteComponent(() => import("./pages/admin-offers"), "AdminOffersPage");
const SellPage = lazyRouteComponent(() => import("./pages/sell"), "SellPage");
const RewardsPage = lazyRouteComponent(() => import("./pages/rewards"), "RewardsPage");
const AdminBenefitsPage = lazyRouteComponent(() => import("./pages/admin-benefits"), "AdminBenefitsPage");
const AdminSubscribersPage = lazyRouteComponent(() => import("./pages/admin-subscribers"), "AdminSubscribersPage");
const AdminTagsPage = lazyRouteComponent(() => import("./pages/admin-tags"), "AdminTagsPage");
const AdminEventsPage = lazyRouteComponent(() => import("./pages/admin-events"), "AdminEventsPage");
const AdminEventAttendeesPage = lazyRouteComponent(() => import("./pages/admin-event-attendees"), "AdminEventAttendeesPage");
const AdminScanPage = lazyRouteComponent(() => import("./pages/admin-scan"), "AdminScanPage");
const TicketPublicPage = lazyRouteComponent(() => import("./pages/ticket-public"), "TicketPublicPage");
const AdminTeamPage = lazyRouteComponent(() => import("./pages/admin-team"), "AdminTeamPage");
const AdminServicesPage = lazyRouteComponent(() => import("./pages/admin-services"), "AdminServicesPage");
const AdminSessionsPage = lazyRouteComponent(() => import("./pages/admin-sessions"), "AdminSessionsPage");
const AdminTrafficPage = lazyRouteComponent(() => import("./pages/admin-traffic"), "AdminTrafficPage");
const AdminFansPage = lazyRouteComponent(() => import("./pages/admin-fans"), "AdminFansPage");
const AdminFanPage = lazyRouteComponent(() => import("./pages/admin-fan"), "AdminFanPage");
const AdminSegmentsPage = lazyRouteComponent(() => import("./pages/admin-segments"), "AdminSegmentsPage");
const AdminSettingsPage = lazyRouteComponent(() => import("./pages/admin-settings"), "AdminSettingsPage");
const AdminCampaignsPage = lazyRouteComponent(() => import("./pages/admin-campaigns"), "AdminCampaignsPage");
const AdminWelcomeEmailPage = lazyRouteComponent(() => import("./pages/admin-welcome-email"), "AdminWelcomeEmailPage");
const AdminLoyaltyPage = lazyRouteComponent(() => import("./pages/admin-loyalty"), "AdminLoyaltyPage");
const AdminMenuPage = lazyRouteComponent(() => import("./pages/admin-menu"), "AdminMenuPage");
const AdminPosPage = lazyRouteComponent(() => import("./pages/admin-pos"), "AdminPosPage");
const AdminFloorPage = lazyRouteComponent(() => import("./pages/admin-floor"), "AdminFloorPage");
const AdminRevenuePage = lazyRouteComponent(() => import("./pages/admin-revenue"), "AdminRevenuePage");
const AdminAnalyticsPage = lazyRouteComponent(() => import("./pages/admin-analytics"), "AdminAnalyticsPage");
const AdminCalendarPage = lazyRouteComponent(() => import("./pages/admin-calendar"), "AdminCalendarPage");
const ForgotPasswordPage = lazyRouteComponent(() => import("./pages/forgot-password"), "ForgotPasswordPage");
const ResetPasswordPage = lazyRouteComponent(() => import("./pages/reset-password"), "ResetPasswordPage");
const BookServicePage = lazyRouteComponent(() => import("./pages/book-service"), "BookServicePage");
const NfcTapPage = lazyRouteComponent(() => import("./pages/nfc-tap"), "NfcTapPage");
const PreferencesPage = lazyRouteComponent(() => import("./pages/preferences"), "PreferencesPage");
const AdminNotificationsPage = lazyRouteComponent(() => import("./pages/admin-notifications"), "AdminNotificationsPage");
const NotificationsPage = lazyRouteComponent(() => import("./pages/notifications"), "NotificationsPage");
const AdminFestivalPage = lazyRouteComponent(() => import("./pages/admin-festival"), "AdminFestivalPage");
const AdminWebsitePage = lazyRouteComponent(() => import("./pages/admin-website"), "AdminWebsitePage");
const AdminMailingListsPage = lazyRouteComponent(() => import("./pages/admin-mailing-lists"), "AdminMailingListsPage");
const AdminOrdersPage = lazyRouteComponent(() => import("./pages/admin-orders"), "AdminOrdersPage");

function RootComponent() {
  useEffect(() => {
    captureReferralCode();
    if (new URLSearchParams(window.location.search).get("subscribed") === "true") {
      clearReferralCode();
    }
  }, []);

  // Record shops render as a WEBSITE in a browser (top nav + hero + footer,
  // document scroll) and as the APP inside the native shell. Everything else
  // uses the app shell as before.
  const { data } = useVenue();
  const website = data?.venue.archetype === "RECORD_SHOP" && !isNativeApp();
  // Festival venues render as a marketing WEBSITE in the browser (top nav +
  // hero + footer), and as the app inside the native shell, mirrors record
  // shops. Which event shows at the root comes from appConfig.landingFestivalEventId.
  const festivalSite = data?.venue.archetype === "FESTIVAL" && !isNativeApp();
  // Radio stations render as a full-bleed marketing WEBSITE (the RadioHome page
  // carries its own header/footer), like festivals + record shops.
  const radioSite = data?.venue.archetype === "RADIO" && !isNativeApp();
  // OPT-IN Assembly-managed website for any OTHER archetype (hospitality,
  // wellness, studio, artist, promoter). Only venues that have explicitly turned
  // this on (appConfig.websiteMode) get the generic website in a browser,
  // everyone else keeps their current app-style home untouched. Never affects an
  // existing live venue unless it opts in. A coming-soon landing page takes
  // precedence, it IS that venue's website (full-page design, e.g. Toka), so
  // don't wrap it in the web chrome/hero.
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const websiteModeValue = data?.venue.appConfig?.websiteMode;
  const websiteMode =
    (websiteModeValue === true || websiteModeValue === "promo") &&
    data?.venue.appConfig?.comingSoon?.enabled !== true &&
    !isNativeApp();

  // Coming-soon landing IS the venue's public site (full-page design, e.g.
  // Toka). At the home path it must render BARE, no venue header, no bottom
  // nav, so it's a clean landing, not the app wrapped around a splash. Other
  // paths (/admin, sign-in, /book) still get their normal chrome so the
  // operator can manage. In the native app we keep the app shell so a launched
  // venue behaves like an app.
  if (data?.venue.appConfig?.comingSoon?.enabled && pathname === "/" && !isNativeApp()) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <ComingSoonSplash />
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (website) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <RecordWebLayout>
            <Outlet />
          </RecordWebLayout>
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (festivalSite) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <FestivalWebLayout>
            <Outlet />
          </FestivalWebLayout>
        </VenueAppShell>
      </DraftGate>
    );
  }

  if (radioSite) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <RadioShell>
            <Outlet />
          </RadioShell>
        </VenueAppShell>
      </DraftGate>
    );
  }

  // MULTI-PAGE white-label site: a venue with websiteMode + configured pages
  // (appConfig.sitePages) serves its whole page set (home + /whats-on +
  // /venue-hire + …) from the block renderer, the builder-designed website
  // (Love Inn first). Any configured slug is intercepted here; other routes
  // (events detail, admin, sign-in) fall through to the layouts below.
  const sitePagesCfg = data?.venue.appConfig?.sitePages;
  if (websiteMode && sitePagesCfg?.pages?.length && (pathname === "/" || pageForPath(sitePagesCfg, pathname))) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <SitePagesSite pathname={pathname} />
        </VenueAppShell>
      </DraftGate>
    );
  }

  // PROMOTER venues that opt in get the bespoke promoter site (MAS port:
  // splash video, crossfade hero, merch, past events, capture) at "/" only.
  if (websiteMode && data?.venue.archetype === "PROMOTER" && pathname === "/") {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <PromoterSite />
        </VenueAppShell>
      </DraftGate>
    );
  }

  // The PROMO SITE (full-bleed hero, release/announcement centrepiece, music,
  // gigs, gallery, capture), designed, not chrome around app cards. ARTIST
  // venues get it as their opted-in website; every other archetype can pick it
  // as a mode (websiteMode: "promo"). The promo page IS the home; every other
  // route (event pages, sign-in…) renders in the generic web layout below.
  if (
    websiteMode &&
    (websiteModeValue === "promo" || data?.venue.archetype === "ARTIST") &&
    (pathname === "/" || pathname === "/gallery" || pathname === "/news")
  ) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          {pathname === "/gallery" ? <ArtistGalleryPage /> : pathname === "/news" ? <ArtistNewsPage /> : <ArtistSite />}
        </VenueAppShell>
      </DraftGate>
    );
  }

  // A venue that has OPTED IN to an Assembly-managed website renders as a generic
  // white-label WEBSITE in a browser (web chrome + document scroll + footer), and
  // as the app inside the native shell. Venues that haven't opted in fall through
  // to their existing app-style home, unchanged.
  if (websiteMode) {
    return (
      <DraftGate>
        <VenueAppShell variant="web">
          <DraftBanner />
          <OfflineBanner />
          <VenueWebLayout>
            <Outlet />
          </VenueWebLayout>
        </VenueAppShell>
      </DraftGate>
    );
  }

  // Native App Store app, the app-shell experience (header + bottom nav).
  return (
    <DraftGate>
      <VenueAppShell>
        <VenueHeader />
        <DraftBanner />
        <OfflineBanner />
        <PullToRefresh>
          <VenueShell>
            <Outlet />
          </VenueShell>
        </PullToRefresh>
        <BottomNav />
      </VenueAppShell>
    </DraftGate>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
});

// Venue root: a festival splash (logo + sign-up) when the venue is a festival
// with a landing event; otherwise the default venue home.
function VenueHome() {
  const { data, isLoading } = useVenue();
  const landingEventId = data?.venue?.appConfig?.landingFestivalEventId;
  // Don't flash the default home before the venue config resolves (usually
  // instant, useVenue seeds from cache).
  if (!data && isLoading) return null;
  // Pre-launch "coming soon" splash (mailing-list capture) takes precedence,
  // a venue that isn't taking bookings/sales yet shows this instead of the home.
  if (data?.venue?.appConfig?.comingSoon?.enabled) return <ComingSoonSplash />;
  if (data?.venue?.archetype === "RADIO") return <RadioHomePage />;
  if (landingEventId) return <FestivalPage eventId={landingEventId} variant="splash" />;
  return <VenuePage />;
}

// A standalone festival page (Lineup / What's on / Info / Tickets), resolves
// the venue's landing event and renders that slice. Falls back to the venue
// home for non-festival venues (these routes are only linked from the festival
// site nav).
function FestivalRoute({ variant }: { variant: FestivalVariant }) {
  const { data } = useVenue();
  const eventId = data?.venue?.appConfig?.landingFestivalEventId;
  if (!eventId) return <VenuePage />;
  return <FestivalPage eventId={eventId} variant={variant} />;
}

// /tickets is the festival buy page for festival venues, and the ticket wallet
// for everyone else.
function TicketsRoute() {
  const { data } = useVenue();
  const eventId = data?.venue?.appConfig?.landingFestivalEventId;
  if (data?.venue?.archetype === "FESTIVAL" && eventId) {
    return <FestivalPage eventId={eventId} variant="tickets" />;
  }
  return <TicketsPage />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: VenueHome,
});

const tiersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tiers",
  component: AllTiersPage,
});

// Radio sub-pages (RADIO venues only). RadioShell (mounted by RootComponent for
// radio venues) provides the player context these consume; guard so a non-radio
// venue hitting these paths just bounces home instead of throwing.
function RadioGuard({ children }: { children: ReactNode }) {
  const { data, isLoading } = useVenue();
  if (!data && isLoading) return null;
  if (data?.venue?.archetype !== "RADIO") return <Navigate to="/" />;
  return <>{children}</>;
}
const radioScheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schedule",
  component: () => (
    <RadioGuard>
      <RadioSchedulePage />
    </RadioGuard>
  ),
});
const radioShowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shows",
  component: () => (
    <RadioGuard>
      <RadioShowsPage />
    </RadioGuard>
  ),
});
const radioShopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop",
  component: () => (
    <RadioGuard>
      <RadioShopPage />
    </RadioGuard>
  ),
});
const radioSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  component: () => (
    <RadioGuard>
      <RadioSupportPage />
    </RadioGuard>
  ),
});

const benefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/benefits",
  component: BenefitsPage,
});

const noticeboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/noticeboard",
  component: NoticeboardPage,
});

const trialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trial",
  component: TrialPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUpPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});

const ticketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets",
  component: TicketsRoute,
});

// Ticket wallet. On non-festival venues /tickets already IS the wallet, but on
// festival venues /tickets is the buy page, so buyers reach their purchased
// tickets here instead (linked from the festival site nav when signed in).
const myTicketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-tickets",
  component: TicketsPage,
});

// Festival website pages (rendered inside FestivalWebLayout for festival venues).
const festivalLineupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lineup",
  component: () => <FestivalRoute variant="lineup" />,
});
const festivalWhatsOnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/whats-on",
  component: () => <FestivalRoute variant="whatsOn" />,
});
const festivalInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/info",
  component: () => <FestivalRoute variant="info" />,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tickets/$id",
  component: TicketDetailPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bookings",
  component: BookingsPage,
});

const bookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/book",
  component: BookEntry,
});

const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/events",
  component: EventsPage,
});

// Artist-site gallery (mosaic) + news (blog). The artist RootComponent branch
// renders them full-bleed; for any non-artist venue these routes bounce home.
const galleryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gallery",
  component: () => <Navigate to="/" />,
});

const newsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/news",
  component: () => <Navigate to="/" />,
});

// Catch-all for builder-defined page slugs (/whats-on, /venue-hire, …): the
// RootComponent's sitePages branch intercepts configured slugs before this
// renders; anything unconfigured bounces home instead of a dead 404.
const sitePageSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: () => <Navigate to="/" />,
});

const eventDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/events/$id",
  component: EventDetailPage,
});

const menuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/menu",
  component: MenuPage,
});

const recordDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/records/$stockId",
  component: RecordDetailPage,
});

const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cart",
  component: CartPage,
});

const wishlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wishlist",
  component: WishlistPage,
});

const adminInventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/inventory",
  component: AdminInventoryPage,
});

const adminStorefrontRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/storefront",
  component: AdminStorefrontPage,
});

const adminRadioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/radio",
  component: AdminRadioPage,
});

const adminOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/offers",
  component: AdminOffersPage,
});

const sellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sell",
  component: SellPage,
});

const rewardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rewards",
  component: RewardsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

const adminBrandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/branding",
  component: AdminBrandingPage,
});

const adminNoticeboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/noticeboard",
  component: AdminNoticeboardPage,
});

const adminBenefitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/benefits",
  component: AdminBenefitsPage,
});

const adminSubscribersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/subscribers",
  component: AdminSubscribersPage,
});

const adminTagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/tags",
  component: AdminTagsPage,
});

const adminEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/events",
  component: AdminEventsPage,
});

const adminEventAttendeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/events/$id/attendees",
  component: AdminEventAttendeesPage,
});

const adminScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/scan",
  component: AdminScanPage,
});

// Public ticket view (no auth), for guests who bought without an account.
const ticketPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ticket/$id",
  component: TicketPublicPage,
});

const adminTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/team",
  component: AdminTeamPage,
});

const adminServicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/services",
  component: AdminServicesPage,
});

const adminSessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/sessions",
  component: AdminSessionsPage,
});

const adminFansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/fans",
  component: AdminFansPage,
});

const adminFanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/fans/$email",
  component: AdminFanPage,
});

const adminSegmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/segments",
  component: AdminSegmentsPage,
});

const adminCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/campaigns",
  component: AdminCampaignsPage,
});

const adminWelcomeEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/welcome-email",
  component: AdminWelcomeEmailPage,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/settings",
  component: AdminSettingsPage,
});

const adminTrafficRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/traffic",
  component: AdminTrafficPage,
});

const adminLoyaltyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/loyalty",
  component: AdminLoyaltyPage,
});

const adminMenuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/menu",
  component: AdminMenuPage,
});
const adminPosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/pos",
  component: AdminPosPage,
});
const adminFloorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/floor",
  component: AdminFloorPage,
});

const adminRevenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/revenue",
  component: AdminRevenuePage,
});

const adminCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/calendar",
  component: AdminCalendarPage,
});

const adminAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/analytics",
  component: AdminAnalyticsPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const bookServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/book-service",
  component: BookServicePage,
});

const nfcTapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$token",
  component: NfcTapPage,
});

const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preferences",
  component: PreferencesPage,
});

const adminNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/notifications",
  component: AdminNotificationsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: NotificationsPage,
});

// A direct /festival/$id link is a "go buy this festival" entry point, so it
// renders the same minimal splash the venue root does (logo + sign-up/buy per
// the venue's configured mode), never the full lineup/marketing scroll. Reads
// the id from the route param (FestivalPage falls back to useParams).
const festivalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/festival/$id",
  component: () => <FestivalPage variant="splash" />,
});

const adminFestivalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/festival",
  component: AdminFestivalPage,
});

const adminWebsiteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/website",
  component: AdminWebsitePage,
});

const adminMailingListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/mailing-lists",
  component: AdminMailingListsPage,
});

const adminOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/orders",
  component: AdminOrdersPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  radioScheduleRoute,
  radioShowsRoute,
  radioShopRoute,
  radioSupportRoute,
  tiersRoute,
  benefitsRoute,
  noticeboardRoute,
  trialRoute,
  signInRoute,
  signUpRoute,
  accountRoute,
  ticketsRoute,
  myTicketsRoute,
  festivalLineupRoute,
  festivalWhatsOnRoute,
  festivalInfoRoute,
  ticketDetailRoute,
  bookingsRoute,
  bookRoute,
  eventsRoute,
  galleryRoute,
  newsRoute,
  sitePageSplatRoute,
  eventDetailRoute,
  menuRoute,
  recordDetailRoute,
  cartRoute,
  wishlistRoute,
  adminInventoryRoute,
  adminStorefrontRoute,
  adminRadioRoute,
  adminOffersRoute,
  sellRoute,
  rewardsRoute,
  profileRoute,
  adminRoute,
  adminBrandingRoute,
  adminNoticeboardRoute,
  adminBenefitsRoute,
  adminSubscribersRoute,
  adminTagsRoute,
  adminEventsRoute,
  adminEventAttendeesRoute,
  adminScanRoute,
  ticketPublicRoute,
  adminTeamRoute,
  adminServicesRoute,
  adminSessionsRoute,
  adminTrafficRoute,
  adminFansRoute,
  adminFanRoute,
  adminSegmentsRoute,
  adminCampaignsRoute,
  adminWelcomeEmailRoute,
  adminSettingsRoute,
  adminLoyaltyRoute,
  adminMenuRoute,
  adminPosRoute,
  adminFloorRoute,
  adminRevenueRoute,
  adminAnalyticsRoute,
  adminCalendarRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  bookServiceRoute,
  nfcTapRoute,
  preferencesRoute,
  adminNotificationsRoute,
  notificationsRoute,
  festivalRoute,
  adminFestivalRoute,
  adminWebsiteRoute,
  adminMailingListsRoute,
  adminOrdersRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
